import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import usePermissions from './usePermissions';
import { listTrades } from '../features/trades/tradesSlice';
import { fetchEvents } from '../features/customEvents/customEventsSlice';

/**
 * Progressive navigation tier hook
 * 
 * Determines which navigation items to show based on user activity level:
 * - Tier 1: Markets, Learn (everyone)
 * - Tier 2: Signals, Strategies (after first strategy or custom event)
 * - Tier 3: Trades, Backtests (after first trade)
 * - Tier 4: Tasks, Assistant, FSM (admin/power users)
 */
const useNavTier = () => {
  const [tier, setTier] = useState(1);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.account.user);
  const strategies = useSelector((state) => state.visualStrategies.strategies);
  const customEvents = useSelector((state) => state.customEvents.events);
  const trades = useSelector((state) => state.trades.items);
  
  const customEventsLoading = useSelector((state) => state.customEvents?.eventsLoading);
  const strategiesLoading = useSelector((state) => state.visualStrategies?.loading);
  
  const { isAdmin } = usePermissions();

  // Refs to track if we've already fetched data (prevents infinite loop for empty arrays)
  const hasFetched = useRef({ trades: false, customEvents: false });

  // Reset hasFetched ref when user switches (user ID changes)
  useEffect(() => {
    if (user?.id) {
      hasFetched.current = { trades: false, customEvents: false };
    }
  }, [user?.id]);

  // Helper to check if user has ever had trades (persists across filtered queries)
  const getHasEverHadTrades = () => {
    const storageKey = user?.id ? `hasHadTrades_${user.id}` : 'hasHadTrades';
    return localStorage.getItem(storageKey) === 'true';
  };

  const setHasHadTrades = () => {
    const storageKey = user?.id ? `hasHadTrades_${user.id}` : 'hasHadTrades';
    localStorage.setItem(storageKey, 'true');
  };

  // Helper to check if user has ever had custom events (persists across filtered queries)
  const getHasEverHadCustomEvents = () => {
    const storageKey = user?.id ? `hasHadCustomEvents_${user.id}` : 'hasHadCustomEvents';
    return localStorage.getItem(storageKey) === 'true';
  };

  const setHasHadCustomEvents = () => {
    const storageKey = user?.id ? `hasHadCustomEvents_${user.id}` : 'hasHadCustomEvents';
    localStorage.setItem(storageKey, 'true');
  };

  // Helper to get scoped localStorage key for showAllFeatures toggle
  const getStorageKey = (userId) => {
    return userId ? `showAllFeatures_${userId}` : 'showAllFeatures';
  };

  // Track when user first gets trades - persist this flag
  useEffect(() => {
    if (trades && trades.length > 0) {
      setHasHadTrades();
    }
  }, [trades]);

  // Track when user first gets custom events - persist this flag
  useEffect(() => {
    if (customEvents && customEvents.length > 0) {
      setHasHadCustomEvents();
    }
  }, [customEvents]);

  // Read from scoped localStorage when user changes or on mount
  useEffect(() => {
    const storageKey = getStorageKey(user?.id);
    setShowAllFeatures(localStorage.getItem(storageKey) === 'true');
  }, [user?.id]);
  
  // Set loading to false once component mounts
  useEffect(() => {
    setLoading(false);
    setInitialLoadComplete(true);
  }, []);
  
  // Check for existing data to determine tier without overwriting shared Redux state
  // Skip fetching trades if we already know from localStorage that the user has had trades —
  // this prevents overwriting state.trades.items and avoids racing with filtered trade queries
  // on the trades page. Only dispatch listTrades() when we genuinely need to discover
  // whether the user has had their first trade.
  useEffect(() => {
    if (user) {
      // Only fetch trades once, and only if we don't already have the persisted flag.
      // If getHasEverHadTrades() is true, tier 3 is already set from localStorage and
      // dispatching listTrades() would only overwrite shared Redux state unnecessarily.
      if (!hasFetched.current.trades && !getHasEverHadTrades()) {
        hasFetched.current.trades = true;
        dispatch(listTrades());
      } else {
        hasFetched.current.trades = true; // mark as handled even if we skip the fetch
      }
      // Only fetch custom events once
      if (!hasFetched.current.customEvents) {
        hasFetched.current.customEvents = true;
        dispatch(fetchEvents({ limit: 100 }));
      }
    }
  }, [user, dispatch]);
  
  // Shared tier computation logic - used by both the effect and toggle
  // Optional override param to avoid stale closure issues when toggling
  const computeTier = (overrideShowAllFeatures) => {
    const effectiveShowAll = overrideShowAllFeatures !== undefined 
      ? overrideShowAllFeatures 
      : showAllFeatures;

    // Tier 4: Admin/power users see everything
    if (user && isAdmin()) {
      return 4;
    }

    // Tier 4: showAllFeatures override (localStorage)
    if (effectiveShowAll) {
      return 4;
    }

    // Tier 3: After first trade logged (Trades, Backtests)
    // Use persisted "has ever had trades" flag — decoupled from Redux loading
    // state so the Trades dropdown never flickers during API calls on other pages
    if (getHasEverHadTrades()) {
      return 3;
    }

    // Tier 2: After first strategy OR custom event (Signals, Strategies)
    // Use persisted "has ever had custom events" flag to avoid reacting to
    // filtered custom events queries from other pages (which may return 0 results)
    if (
      (!strategiesLoading && strategies && strategies.length > 0) ||
      (!customEventsLoading && getHasEverHadCustomEvents())
    ) {
      return 2;
    }

    // Default: Tier 1
    return 1;
  };

  // Calculate tier based on user activity
  useEffect(() => {
    if (loading) return;

    setTier(computeTier());
  }, [user, strategies, customEvents, trades, isAdmin, showAllFeatures, loading, strategiesLoading, customEventsLoading]);
  
  // Toggle function for "Show all features" - only write to localStorage on explicit toggle
  const toggleShowAllFeatures = () => {
    const newValue = !showAllFeatures;
    setShowAllFeatures(newValue);
    // Only write to localStorage on explicit user toggle, not on initial load
    // Use scoped key to prevent different users on same device inheriting preferences
    if (initialLoadComplete) {
      localStorage.setItem(getStorageKey(user?.id), newValue.toString());
    }
    // Pass new value explicitly to avoid stale closure flash
    setTier(computeTier(newValue));
  };
  
  return {
    tier, // 1, 2, 3, or 4
    showAllFeatures,
    toggleShowAllFeatures,
    loading,
  };
};

export default useNavTier;
